import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import 'dayjs/locale/pt-br.js';
import { notificationService } from '../services/notificationService.js';
import toast from 'react-hot-toast';
import './Notifications.css';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

function Notifications({ notifications: propNotifications, onNotificationsUpdate }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAsRead, setMarkingAsRead] = useState(null);

  useEffect(() => {
    if (propNotifications) {
      setNotifications(propNotifications);
      setLoading(false);
    } else {
      loadNotifications();
    }
  }, [propNotifications]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const result = await notificationService.getNotifications();
      
      if (result.success) {
        setNotifications(result.notifications);
        if (onNotificationsUpdate) {
          onNotificationsUpdate(result.notifications);
        }
      } else {
        toast.error('Erro ao carregar notificações');
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      toast.error('Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      setMarkingAsRead(notificationId);
      const result = await notificationService.markAsRead(notificationId);
      
      if (result.success) {
        // Atualizar a notificação localmente
        const updatedNotifications = notifications.map(notif => 
          notif.id === notificationId 
            ? { ...notif, isOpened: true }
            : notif
        );
        setNotifications(updatedNotifications);
        
        // Notificar o Dashboard sobre a atualização
        if (onNotificationsUpdate) {
          onNotificationsUpdate(updatedNotifications);
        }
      } else {
        toast.error('Erro ao marcar como lida');
      }
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
      toast.error('Erro ao marcar como lida');
    } finally {
      setMarkingAsRead(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const result = await notificationService.markAllAsRead();
      
      if (result.success) {
        // Atualizar todas as notificações localmente
        const updatedNotifications = notifications.map(notif => ({ ...notif, isOpened: true }));
        setNotifications(updatedNotifications);
        
        // Notificar o Dashboard sobre a atualização
        if (onNotificationsUpdate) {
          onNotificationsUpdate(updatedNotifications);
        }
        
        toast.success('Todas as notificações foram marcadas como lidas');
      } else {
        toast.error('Erro ao marcar todas como lidas');
      }
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
      toast.error('Erro ao marcar todas como lidas');
    }
  };

  const unreadCount = notifications.filter(n => !n.isOpened).length;

  if (loading) {
    return (
      <div>
        <div className="notifications-header">
          <h1 className="notifications-title">Notificações</h1>
        </div>
        <div className="notifications-loading">
          <div className="spinner"></div>
          <p>Carregando notificações...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="notifications-header">
        <div className="notifications-header-left">
          <h1 className="notifications-title">Todas as notificações</h1>
          {unreadCount > 0 && (
            <span className="notifications-badge">{unreadCount} {unreadCount === 1 ? 'nova' : 'novas'}</span>
          )}
        </div>
        {unreadCount > 0 && (
          <button 
            className="mark-all-read-btn"
            onClick={handleMarkAllAsRead}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Marcar todas como lidas
          </button>
        )}
      </div>

      <div className="notifications-list">
        {notifications.length === 0 ? (
          <div className="notifications-empty">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            <h3>Nenhuma notificação</h3>
            <p>Não há notificações no momento.</p>
          </div>
        ) : (
          notifications.map(notification => (
            <div 
              key={notification.id} 
              className={`notification-item ${!notification.isOpened ? 'unread' : ''}`}
            >
              <div className="notification-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
              </div>
              <div className="notification-content">
                <div className="notification-header-row">
                  <h3 className="notification-title">{notification.title}</h3>
                  {!notification.isOpened && <span className="unread-indicator"></span>}
                </div>
                <p className="notification-message">{notification.message}</p>
                <div className="notification-footer">
                  <span className="notification-time">
                    {dayjs(notification.createdAt).fromNow()}
                  </span>
                  {!notification.isOpened && (
                    <button
                      className="mark-read-btn"
                      onClick={() => handleMarkAsRead(notification.id)}
                      disabled={markingAsRead === notification.id}
                    >
                      {markingAsRead === notification.id ? 'Marcando...' : 'Marcar como lida'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Notifications;
